import React, { useEffect } from 'react';
import {
  Divider,
  Typography,
  makeStyles,
  Button,
  Grid,
  Paper,
} from '@material-ui/core';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from 'store';
import { PageToolbar } from 'components/PageToolbar';
import { useDispatch, useSelector } from 'react-redux';

import { stackActions } from 'actions/stackActions';
import { SectionView } from 'features/stacks/stackDetail/SectionView';
import { departmentActions } from 'actions/departmentActions';

import { StackStateEnumDto } from '.generated/api';
import PageSpinner from 'components/spinners/PageSpinner';
import StackFileLinks from 'components/StackFileLinks';

interface ParamTypes {
  orgId: string;
  deptId: string;
  stackId: string;
}

const useStyles = makeStyles(() => ({
  toolbarTitle: {
    minHeight: '1.8rem',
  },
  content: {
    padding: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
  },
  buttonClass: {
    marginLeft: 0,
  },
  alignRight: {
    textAlign: 'right',
  },
  paper: {
    padding: 0,
  },
}));

const Stack = (): React.ReactElement => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation(['translations']);
  const { stackId, deptId } = useParams<ParamTypes>();

  const department = useSelector(
    (state: ApplicationState) => state.department.department,
  );
  const stack = useSelector((state: ApplicationState) => state.stack);

  const handleBack = (): void => {
    history.goBack();
  };

  async function handleDownloadReport() {
    if (stackId) {
      dispatch(stackActions.downloadStackArchive([stackId]));
    }
  }

  useEffect(() => {
    dispatch(stackActions.getStackById(stackId));
  }, []);

  useEffect(() => {
    dispatch(departmentActions.clearDepartment());
    if (deptId) {
      dispatch(departmentActions.getDepartmentById(deptId));
    }
  }, [deptId]);

  return (
    <>
      <PageToolbar>
        <Typography variant="subtitle1" className={classes.toolbarTitle}>
          {department && department.name}
        </Typography>
        <Divider />
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="baseline"
        >
          <Grid item xs>
            <Button
              variant="contained"
              color="primary"
              className={classes.buttonClass}
              onClick={handleBack}
            >
              {t('common.back')}
            </Button>
          </Grid>
          <Grid item xs className={classes.alignRight}>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={classes.buttonClass}
                onClick={handleDownloadReport}
                disabled={
                  stack.stack?.status === StackStateEnumDto.PRE_REGISTRATION
                }
              >
                {t('stack_page.get_report')}
              </Button>
            </div>
          </Grid>
        </Grid>
      </PageToolbar>
      <PageSpinner isLoading={!stack.sections}>
        <div className={classes.content}>
          <Paper className={classes.paper}>
            {stack.sections?.map((section, i) => (
              <Grid key={i} item>
                <SectionView section={section}></SectionView>
              </Grid>
            ))}
          </Paper>
          {stackId && <StackFileLinks stackId={stackId} />}
        </div>
      </PageSpinner>
    </>
  );
};

export { Stack };
